import { Suspense, useContext, useState } from 'react';
import { LazyLoader, Modal } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { GroupSettingsForm } from '../../../components/GroupSettingsForm/GroupSettingsForm';
import { GroupForm } from '../../../components/GroupForm/GroupForm';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useNavigate } from 'react-router-dom';
import { GroupFormOther } from '../../../components/GroupForm/GroupFormOther';
import { updateClaims } from '../../../utils/authStore';
import { graphql } from 'client/gql';
import { useMutation, useQuery } from 'urql';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { GroupType } from 'client/gql/graphql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { InstitutionGroupsTable } from 'components/Groups/tables/InstitutionGroupsTable';
import { showUserDbErrorToast, showSuccessDeleteToast } from 'utils/toasts';
import { useGroups } from 'components/Groups/hooks/useGroups';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupFromInstitutionGroups($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

export type GroupSettings = {
  cloudDirectory: boolean;
  asThreemaGroup: boolean;
  allowViewersCanAddMaterial: boolean;
  allowViewersCanCreateEvents: boolean;
  allowViewersCanStartMeeting: boolean;
};

const InstitutionGroupsSubpageQueryDocument = graphql(`
  query InstitutionGroupsSubpageData(
    $groupWhere: GroupWhere!
    $calendarEventWhere: CalendarEventWhere!
    $profile: ID!
  ) {
    ...PermissionCheckerFields
    ...UseGroupsFields
  }
`);

export const InstitutionGroupsSubpage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);
  const { pimAuthClaims } = useAuthClaims();
  const profile = pimAuthClaims.getProfile();
  const organization = pimAuthClaims.getOrganization();
  const profileUuid = profile.uuid;
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [, deleteGroup] = useMutation(DeleteGroupMutationDocument);

  const { ConfirmationDialog, confirmDelete } = useGroups();

  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedYearUuid, setSelectedYearUuid] = useState<string>(currentSchoolYear.uuid);
  const [showSpanning, setShowSpanning] = useState<boolean>(true);
  const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [formProfilesOpen, setFormProfilesOpen] = useState<boolean>(false);
  const [currentGroupUuid, setCurrentGroupUuid] = useState<string>('');

  const [{ data }] = useQuery({
    query: InstitutionGroupsSubpageQueryDocument,
    variables: {
      profile: profileUuid,
      groupWhere: {
        type: GroupType.Group,
        organization: { uuid: organization.uuid },
        AND: [
          {
            OR: [{ schoolYear: { uuid: selectedYearUuid } }, { schoolYear: showSpanning ? null : undefined }],
          },
        ],
      },
      calendarEventWhere: {
        holderConnection: {
          Group: {
            node: {
              organization: { uuid: organization.uuid },
              type: GroupType.Group,
              AND: [
                {
                  OR: [{ schoolYear: { uuid: selectedYearUuid } }, { schoolYear: showSpanning ? null : undefined }],
                },
              ],
            },
          },
        },
        virtualLocations_SOME: {
          running: true,
        },
      },
    },
  });

  const permissions = usePermissionChecker(data);
  const canCollaborate = permissions?.canUpdateCollaborationGroups(organization);

  const handleAdd = () => {
    setFormModalOpen(true);
    setCurrentGroupUuid('');
  };

  const handleEdit = (uuid: string) => {
    setFormModalOpen(true);
    setCurrentGroupUuid(uuid);
  };

  const handleProfilesEdit = (uuid: string) => {
    setFormProfilesOpen(true);
    setCurrentGroupUuid(uuid);
  };

  async function handleDelete(uuid: string) {
    const result = await confirmDelete();
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteGroup({ id: uuid });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  }

  const handleSettings = (groupUuid: string) => {
    setSettingsModalOpen(true);
    setCurrentGroupUuid(groupUuid);
  };

  const handleSettingsClose = () => {
    setSettingsModalOpen(false);
    setCurrentGroupUuid('');
  };

  function handleToggleSpanning() {
    setShowSpanning(!showSpanning);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canListInstitutionGroups(organization) || !organization}>
      <InstitutionGroupsTable
        query={data}
        permissions={permissions}
        loading={tableLoading}
        yearUuid={selectedYearUuid}
        showSpanning={showSpanning}
        onYearSelect={setSelectedYearUuid}
        onToggleSpanning={handleToggleSpanning}
        onNavigate={(uuid) => navigate('/groups/' + uuid)}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onEditProfiles={handleProfilesEdit}
        onDelete={async (uuid) => handleDelete(uuid)}
        onSettings={(uuid) => handleSettings(uuid)}
      />

      <Modal
        isOpen={formModalOpen}
        onRequestClose={() => {
          setFormModalOpen(false);
          setCurrentGroupUuid('');
        }}
        title={currentGroupUuid !== '' ? t('groups.editGroup') : t('groups.addGroup')}
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
          <GroupForm
            canCollaborate={!!canCollaborate}
            groupUuid={currentGroupUuid}
            onClose={async () => {
              await updateClaims(() => setClaimsTrigger(!claimsTrigger));
              setFormModalOpen(false);
              setCurrentGroupUuid('');
            }}
          />
        </Suspense>
      </Modal>

      <Modal
        isOpen={formProfilesOpen}
        onRequestClose={() => {
          setFormProfilesOpen(false);
          setCurrentGroupUuid('');
        }}
        title={t('groups.editGroup')}
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
          <GroupFormOther
            groupUuid={currentGroupUuid}
            onClose={async () => {
              await updateClaims(() => setClaimsTrigger(!claimsTrigger));
              setFormProfilesOpen(false);
              setCurrentGroupUuid('');
            }}
          />
        </Suspense>
      </Modal>

      <Modal
        title={t('groups.groupsSettings')}
        isOpen={settingsModalOpen}
        onRequestClose={handleSettingsClose}
        width='s'
      >
        <GroupSettingsForm onClose={handleSettingsClose} groupUuid={currentGroupUuid} />
      </Modal>

      <ConfirmationDialog />
    </BpSubpage>
  );
};
